import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Switch,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import api from '../api';

export default function CurrencyModal({ isOpen, onClose }) {
  const [currencies, setCurrencies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', symbol: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchCurrencies = useCallback(async () => {
    try { const { data } = await api.get('/bank-accounts/currencies'); setCurrencies(data); } catch(e) {}
  }, []);

  useEffect(() => { if (isOpen) { fetchCurrencies(); setShowForm(false); } }, [isOpen, fetchCurrencies]);

  const handleSave = async () => {
    if (!form.code || !form.name) { toast({ title: 'Code and name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post('/bank-accounts/currencies', form);
      await fetchCurrencies();
      setForm({ code: '', name: '', symbol: '' });
      setShowForm(false);
      toast({ title: 'Currency added', status: 'success', duration: 1500 });
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            Currencies
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Add'}</Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Code *</FormLabel>
                    <Input size="sm" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
                      placeholder="e.g. GBP" />
                  </FormControl>
                  <FormControl flex={2}>
                    <FormLabel fontSize="xs" mb={0.5}>Name *</FormLabel>
                    <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="British Pound Sterling" />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Symbol</FormLabel>
                    <Input size="sm" value={form.symbol} onChange={e => setForm(p => ({ ...p, symbol: e.target.value }))}
                      placeholder="£" />
                  </FormControl>
                </HStack>
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">Add Currency</Button>
              </VStack>
            </Box>
          )}

          {currencies.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No currencies configured.</Text>
          ) : (
            <VStack spacing={1} align="stretch">
              {currencies.map(c => (
                <Flex key={c.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                  align="center" justify="space-between">
                  <HStack spacing={3}>
                    <Badge fontSize="10px" colorScheme="blue" variant="solid" minW="40px" textAlign="center">{c.code}</Badge>
                    {c.symbol && <Text fontSize="13px" fontWeight="600">{c.symbol}</Text>}
                    <Text fontSize="12px">{c.name}</Text>
                    {!!c.is_default && <Badge fontSize="8px" colorScheme="green">Default</Badge>}
                  </HStack>
                  <Button size="xs" variant={c.is_default ? 'solid' : 'outline'} colorScheme={c.is_default ? 'green' : 'gray'}
                    fontSize="9px" onClick={async () => {
                      try { await api.put(`/bank-accounts/currencies/${c.id}/default`); await fetchCurrencies();
                        toast({ title: `${c.code} set as default`, status: 'success', duration: 1500 });
                      } catch(e) {}
                    }}>{c.is_default ? 'Default' : 'Set Default'}</Button>
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
